/**
 * chrome.storage.local wrapper with safe fallback to localStorage for dev mode
 * (when running outside of an extension context, e.g. `vite dev`).
 */

type StorageBackend = {
  get<T>(key: string): Promise<T | undefined>;
  set<T>(key: string, value: T): Promise<void>;
  remove(key: string): Promise<void>;
  onChange(key: string, listener: (next: unknown) => void): () => void;
};

const isExtension =
  typeof chrome !== 'undefined' &&
  typeof chrome.storage !== 'undefined' &&
  typeof chrome.storage.local !== 'undefined';

const chromeBackend: StorageBackend = {
  async get<T>(key: string) {
    const result = await chrome.storage.local.get(key);
    return result[key] as T | undefined;
  },
  async set<T>(key: string, value: T) {
    await chrome.storage.local.set({ [key]: value });
  },
  async remove(key: string) {
    await chrome.storage.local.remove(key);
  },
  onChange(key, listener) {
    const handler = (
      changes: { [k: string]: chrome.storage.StorageChange },
      area: string,
    ) => {
      if (area !== 'local') return;
      if (key in changes) listener(changes[key]!.newValue);
    };
    chrome.storage.onChanged.addListener(handler);
    return () => chrome.storage.onChanged.removeListener(handler);
  },
};

const localBackend: StorageBackend = {
  async get<T>(key: string) {
    const raw = localStorage.getItem(key);
    if (raw == null) return undefined;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return undefined;
    }
  },
  async set<T>(key: string, value: T) {
    localStorage.setItem(key, JSON.stringify(value));
    window.dispatchEvent(
      new StorageEvent('storage', { key, newValue: JSON.stringify(value) }),
    );
  },
  async remove(key: string) {
    localStorage.removeItem(key);
  },
  onChange(key, listener) {
    const handler = (event: StorageEvent) => {
      if (event.key !== key) return;
      try {
        listener(event.newValue ? JSON.parse(event.newValue) : undefined);
      } catch {
        listener(undefined);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  },
};

export const storage: StorageBackend = isExtension ? chromeBackend : localBackend;
